import { useCallback, useEffect, useMemo, useState } from 'react'

import { useSelector } from 'react-redux'
import { debounce, isArray, isFunction } from 'lodash'
import { Metadata, retrieveMetadata } from '../utils/http'
import { selectUser } from '../reducers/UserReducer'

const DEFAULT_DATA = { activeKey: 1, payload: {} }
const DEFAULT_PAGE_SIZE = 20

/**
 * Hook that handles api calls with pagination and caching.
 * @param {object} params
 * @param {(user:object, parameters:object) => any} params.request
 * @param {object} [params.defaultParameters]
 * @param {Metadata} [params.defaultMetadata]
 * @param {(data:any)=>unknown} [params.select]
 */
const useQuery = ({ request, defaultParameters = {}, defaultMetadata = new Metadata(), select }) => {
  const user = useSelector(selectUser)

  const [data, setData] = useState(DEFAULT_DATA)
  const [metadata, setMetadata] = useState(defaultMetadata)
  const [parameters, setParameters] = useState(defaultParameters)
  const [loading, setLoading] = useState(false)

  const wrappedRequest = useCallback(() => {
    const pagination = metadata.toQueryParameters()

    if (!isArray(data.payload[pagination.page])) {
      setLoading(true)
      const promise = request(user, { ...parameters, ...pagination })

      if (promise instanceof Promise) {
        promise.then(json => {
          if (json?.data) {
            setData({
              ...data,
              payload: { ...data.payload, [pagination.page]: json.data },
              activeKey: pagination.page
            })
          }

          if (json?.meta) {
            setMetadata(retrieveMetadata(json.meta))
          }

          setLoading(false)
        })
      } else {
        setLoading(false)
      }
    }
  }, [request, parameters, data, metadata.page, metadata.pageSize, user.id, setMetadata, setData, setLoading])

  const refetch = useCallback(() => {
    setData(DEFAULT_DATA)
    setMetadata(new Metadata({ ...metadata }))
  }, [metadata])

  const next = useCallback(() => {
    if (metadata.next <= metadata.last) {
      setMetadata(new Metadata({
        ...metadata,
        page: metadata.next,
        prev: metadata.page,
        next: metadata.next + 1
      }))
    }
  }, [metadata])

  const previous = useCallback(() => {
    if (metadata.prev >= metadata.first) {
      setMetadata(new Metadata({
        ...metadata,
        page: metadata.prev,
        prev: metadata.prev - 1,
        next: metadata.page
      }))
    }
  }, [metadata])

  const first = useCallback(() => {
    setMetadata(new Metadata({ ...metadata, prev: metadata.first, next: metadata.first + 1, page: metadata.first }))
  }, [metadata])

  const last = useCallback(() => {
    setMetadata(new Metadata({ ...metadata, prev: metadata.last - 1, next: metadata.last, page: metadata.last }))
  }, [metadata])

  useEffect(() => {
    const fetch = debounce(wrappedRequest, 300)

    fetch()

    return () => fetch.cancel()
  }, [wrappedRequest])

  const mutate = useCallback(mutator => {
    const newData = { ...data }

    newData.payload[newData.activeKey] = mutator(newData.payload[newData.activeKey])

    setData(newData)
  }, [data, setData])

  const mutateParameters = useCallback(newParameters => {
    setParameters({ ...parameters, ...newParameters })
    setMetadata(new Metadata({
      ...metadata,
      page: metadata.first,
      pageSize: newParameters?.pageSize ?? DEFAULT_PAGE_SIZE
    }))

    setData({ ...DEFAULT_DATA })
    setLoading(true)
  }, [setParameters, setMetadata, setData, setLoading])

  const returnValue = useMemo(() => {
    const toReturn = data.payload[metadata.page] ?? []

    return isFunction(select) ? select(toReturn) : toReturn
  }, [data.payload, metadata.page, select])

  return {
    all: data.payload,
    data: returnValue,
    parameters,
    metadata,
    loading: (data.payload[metadata.page] ? loading : true),
    setMetadata,
    setParameters: mutateParameters,
    refetch,
    setData,
    next,
    previous,
    first,
    last,
    mutate
  }
}

export default useQuery
